import React, { useState, useEffect } from "react";
import RecordBookService from "../services/recordBookService";
import { AuthContext } from "../store/ContextStore";
import { useContext } from "react";


interface RecordItem {
  entity: string;
  stat_value: string | number;
  period:string
  data_source?: string

}
function Records() {

  const { selectedSport } = useContext(AuthContext);
  
  const [type, setType] = useState(""); // Player / Team
  const [category, setCategory] = useState("");
  const [period, setPeriod] = useState("");
  const [teamName, setTeamName] = useState("");
  const[teamCode, setTeamCode] = useState("");
  // const [teamName, setTeamName] = useState("");  
  const [results, setResults] = useState<RecordItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statColumns, setStatColumns] = useState<string[]>([]); // dynamic columns
  const [categories, setCategories] = useState<string[]>([]);


  // ✅ NEW: Grouped records
  const [groupedResults, setGroupedResults] = useState<Record<string, any[]> | null>(null);

// Load team name
useEffect(() => {
  const data = localStorage.getItem("sid_user");
  if (data) {
    try {
      const parsed = JSON.parse(data);
      if (parsed?.team?.code) {
        setTeamCode(parsed.team.code);
        setTeamName(parsed.team.name);
      }
    } catch (err) {
      console.error("Failed to parse sid_user from localStorage:", err);
    }
  }
}, []);


useEffect(() => {
  if (!selectedSport || !type) return;

  const fetchCategories = async () => {
    try {
      const cats = await RecordBookService.getRecordCategories(selectedSport, type);
      setCategories(cats);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      setCategories([]);
    }
  };

  fetchCategories();
}, [selectedSport, type]);

  const allFiltersSelected = category && period && type && teamCode;

  const fetchResults = async () => {
    if (!allFiltersSelected) return;
    setLoading(true);
    setError(null);

    try {
      const response = await RecordBookService.getRecordPlayers({
        SportCode: selectedSport,
        Category: category,
        Period: period,
        Entity: type,
        TeamCode: teamCode,
      });

      if (!response.players || response.players.length === 0) {
        setResults([]);
        setStatColumns([]);
        setGroupedResults(null);
        setLoading(false);
        return;
      }

      // ✅ Check if payload has stat_name + stat_value (stat-wise response)
      if ("stat_name" in response.players[0] && "stat_value" in response.players[0]) {
        // Group by stat_name
        const grouped: Record<string, any[]> = {};
        response.players.forEach((item: any) => {
          if (!grouped[item.stat_name]) grouped[item.stat_name] = [];
          grouped[item.stat_name].push(item);
        });
        setGroupedResults(grouped);
        setResults([]); // clear old style
        setStatColumns([]);
      } else {
        // 🔹 Old payload (multi-column)
        const columnsSet = new Set<string>();
        response.players.forEach((player: any) => {
          Object.keys(player).forEach((key) => {
            if (key !== "player_name") columnsSet.add(key);
          });
        });

        const columns = Array.from(columnsSet);
        const orderedColumns = columns.includes("data_source")
          ? ["data_source", ...columns.filter((c) => c !== "data_source")]
          : columns;

        setStatColumns(orderedColumns);
        setResults(response.players);
        setGroupedResults(null);
      }
    } catch (err) {
      console.error("Failed to fetch results:", err);
      setError("Failed to load records. Please try again.");
      setResults([]);
      setStatColumns([]);
      setGroupedResults(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allFiltersSelected) {
      setResults([]);
      setStatColumns([]);
      setGroupedResults(null);
      fetchResults();
    }
  }, [category, period, type, teamCode]);

  return (
    <div className="flex gap-8 p-6 min-h-screen">
      {/* Left Sidebar */}
      <div className="w-80 flex-shrink-0">
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Filters</h2>
          <div className="space-y-6">
            {/* Type */}
            <div>
              <label className="block mb-3 font-semibold text-gray-700">Select Type:</label>
              <select
                value={type}
                onChange={(e) => {
                  const newType = e.target.value;
                  setType(newType);

                  // 🔹 Reset dependent filters when type changes
                  setCategory("");
                  setPeriod("");
                  setResults([]);
                  setGroupedResults(null);
                  setStatColumns([]);
                }}
                className="w-full border p-3 rounded-lg"
              >
                <option value="">-- Choose Type --</option>
                <option value="Player">Player</option>
                <option value="Team">Team</option>
              </select>
            </div>

            {/* Category */}
            {type && (
              <div>
                <label className="block mb-3 font-semibold text-gray-700">Stat Category:</label>
               <select
  value={category}
  onChange={(e) => {
    setCategory(e.target.value);
    setPeriod(""); // reset period when category changes
    setResults([]);
    setGroupedResults(null);
    setStatColumns([]);
  }}
  className="w-full border p-3 rounded-lg"
>
  <option value="">-- Choose Category --</option>
  {categories.map((cat) => (
    <option key={cat} value={cat}>{cat}</option>
  ))}
</select>

              </div>
            )}

            {/* Period */}
            {category && (
              <div>
                <label className="block mb-3 font-semibold text-gray-700">Stat Period:</label>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="w-full border p-3 rounded-lg"
                >
                  <option value="">-- Choose Period --</option>
                  <option value="game">Game</option>
                  <option value="season">Season</option>
                  {type !== "Team" && <option value="career">Career</option>}
                </select>
              </div>
            )}

            {teamName && (
              <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  <span className="font-semibold">Team:</span> {teamName}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right - Table */}
      <div className="flex-1 flex justify-center">
        {allFiltersSelected ? (
          <div className="w-full max-w-6xl">
            <h2 className="text-2xl font-bold mb-6 text-gray-800 text-center">Results</h2>

            {/* ✅ New grouped rendering */}
            {groupedResults ? (
              Object.entries(groupedResults).map(([statName, rows]) => {
                const showDataSource = (rows as any[])?.some((r) => r?.data_source != null);

                return (
                  <div key={statName} className="mb-10">
                    <h3 className="text-xl font-semibold text-gray-700 mb-4">
                      Stat: {statName}
                    </h3>
                    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-auto">
                      <table className="w-full min-w-max">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="border px-4 py-2 text-left font-semibold">
                              {type === "Player" ? "Player Name" : "Team Name"}
                            </th>
                            <th className="border px-4 py-2 text-left font-semibold">Value</th>
                            {showDataSource && (
                              <th className="border px-4 py-2 text-left font-semibold">Data Source</th>
                            )}
                            {(period === "game" || period === "season") && (
                              <th className="border px-4 py-2 text-left font-semibold">
                                {period === "game" ? "Game Date" : "Season"}
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {(rows as any[]).map((player, idx) => (
                            <tr key={idx} className="hover:bg-gray-50 transition-colors">
                              <td className="border px-4 py-2">{player.entity_name}</td>
                              <td className="border px-4 py-2">{player.stat_value}</td>
                              {showDataSource && (
                                <td className="border px-4 py-2">{player.data_source ?? "-"}</td>
                              )}
                              {(period === "game" || period === "season") && (
                                <td className="border px-4 py-2">{player.period}</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })
            ) : results.length > 0 ? (
              // 🔹 Old table logic (unchanged)
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-auto">
                <table className="w-full min-w-max">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="border px-4 py-2 text-left font-semibold">Player Name</th>
                      {statColumns.map((col) => (
                        <th key={col} className="border px-4 py-2 text-left font-semibold">
                          {col === "data_source" ? "Data Source" : col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((player, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 transition-colors">
                        <td className="border px-4 py-2">{player.player_name}</td>
                        {statColumns.map((col) => (
                          <td key={col} className="border px-4 py-2">{player[col] ?? "-"}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700 text-center">{error}</div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-gray-500 text-center">
                {loading ? "Loading..." : "No records found."}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="text-gray-400 text-6xl mb-4">📊</div>
              <p className="text-gray-500 text-lg">Select all filters to view records</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Records;